import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import pointIcon from '../../assets/images/pedometer/icon-point.png';
import snackIcon from '../../assets/images/pedometer/icon-snack.png';
import specialIcon from '../../assets/images/pedometer/icon-special.png';

/** 접힘 상태에서 머리 칸 / 다음 칸 / 마지막 칸이 놓이는 가로 비율 */
const PA = 0.13;
const PB = 0.3;
const PC = 0.87;
const PITCH_RATIO = 0.175;
const TAP_SLOP = 6;
const EXPAND_SLOP = 8;
const COLLAPSE_SLOP = 24;
/** 씬 가로의 90.8%가 레일이므로, 레일 폭을 씬 기준 1% 단위로 되돌린다 */
const SCENE_RATIO = 90.8;

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function statusClass(status) {
  switch (status) {
    case 'DONE':
      return 'is-done';
    case 'OPEN':
      return 'is-open';
    case 'READY':
      return 'is-ready';
    default:
      return 'is-lock';
  }
}

function iconOf(segment, isLast) {
  if (isLast) return specialIcon;
  return segment.rewardType === 'SNACK' ? snackIcon : pointIcon;
}

function PedometerRewardRail({ segments, headIndex, steps, onSegmentTap, className, style }) {
  const railRef = useRef(null);
  const dragRef = useRef({ dragging: false, startX: 0, startOff: 0, moved: 0, expandGesture: false });
  const offRef = useRef(0);
  const [width, setWidth] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [off, setOff] = useState(0);

  useLayoutEffect(() => {
    const rail = railRef.current;
    if (!rail) return undefined;

    const measure = () => setWidth(rail.clientWidth);
    measure();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }

    const observer = new ResizeObserver(measure);
    observer.observe(rail);
    return () => observer.disconnect();
  }, []);

  const applyOff = useCallback((next) => {
    offRef.current = next;
    setOff(next);
  }, []);

  // 보상을 받아 머리 칸이 옮겨가면 다시 3칸으로 접는다
  useEffect(() => {
    setExpanded(false);
    applyOff(0);
  }, [applyOff, headIndex]);

  const lastIndex = segments.length - 1;
  const pitch = width * PITCH_RATIO;
  const maxOff = Math.max(0, width * PA + lastIndex * pitch + width * 0.13 - width);
  // 전부 받은 날은 접을 머리 칸이 없으므로 완주한 레일을 그대로 보여준다
  const allDone = lastIndex >= 0 && segments[lastIndex].status === 'DONE';
  const expandedView = expanded || allDone;

  const xOf = (index) => {
    if (expandedView) return width * PA + index * pitch;
    const gap = index - headIndex;
    if (gap === 0) return width * PA;
    if (index === lastIndex) return width * PC;
    if (gap === 1) return width * PB;
    return (width * (PB + PC)) / 2;
  };

  const isHidden = (index) => !expandedView && index - headIndex > 1 && index < lastIndex;

  const onPointerDown = (event) => {
    dragRef.current = {
      dragging: true,
      startX: event.clientX,
      startOff: offRef.current,
      moved: 0,
      expandGesture: false,
    };
    setDragging(true);
    try {
      railRef.current?.setPointerCapture(event.pointerId);
    } catch (error) {
      // 포인터 캡처를 지원하지 않는 환경
    }
  };

  const onPointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag.dragging) return;

    const dx = event.clientX - drag.startX;
    drag.moved = Math.max(drag.moved, Math.abs(dx));

    if (!expandedView && (dx < -EXPAND_SLOP || (dx > EXPAND_SLOP && headIndex > 0))) {
      const baseOff = clamp(headIndex * pitch, 0, maxOff);
      drag.expandGesture = true;
      drag.startOff = baseOff + dx;
      setExpanded(true);
      applyOff(baseOff);
      return;
    }

    applyOff(clamp(drag.startOff - dx, 0, expandedView ? maxOff : 0));
  };

  const endDrag = (event) => {
    const drag = dragRef.current;
    if (!drag.dragging) return;
    drag.dragging = false;
    setDragging(false);
    try {
      railRef.current?.releasePointerCapture(event.pointerId);
    } catch (error) {
      // no-op
    }

    const dx = (event.clientX ?? drag.startX) - drag.startX;
    if (expandedView && !drag.expandGesture && offRef.current <= 0 && dx > COLLAPSE_SLOP) {
      setExpanded(false);
      applyOff(0);
    }
  };

  const handleNodeClick = (segment) => () => {
    if (dragRef.current.moved > TAP_SLOP) return;
    onSegmentTap?.(segment);
  };

  const trackStart = expandedView ? xOf(0) : width * 0.05;
  const trackEnd = xOf(lastIndex);

  // 초록 채움 = 걸음 수 진행도 (보상 수령/headIndex와 무관)
  // 펼침: 전체 마일스톤 x 보간 / 접힘: 보이는 앵커(머리·다음·마지막) 기준 보간
  let fillEnd = trackStart;
  if (width > 0 && segments.length > 0) {
    const goalSteps = segments[lastIndex].steps || 1;
    if (steps >= goalSteps) {
      fillEnd = trackEnd;
    } else if (steps > 0) {
      const anchors = [{ at: 0, x: trackStart }];
      if (expandedView) {
        segments.forEach((segment, index) => {
          anchors.push({ at: segment.steps, x: xOf(index) });
        });
      } else {
        anchors.push({ at: segments[headIndex].steps, x: xOf(headIndex) });
        if (headIndex + 1 < lastIndex) {
          anchors.push({ at: segments[headIndex + 1].steps, x: xOf(headIndex + 1) });
        }
        if (lastIndex > headIndex) {
          anchors.push({ at: goalSteps, x: trackEnd });
        }
      }

      let from = anchors[0];
      let to = anchors[anchors.length - 1];
      for (let i = 1; i < anchors.length; i += 1) {
        if (steps <= anchors[i].at) {
          from = anchors[i - 1];
          to = anchors[i];
          break;
        }
        from = anchors[i];
      }
      const span = to.at - from.at || 1;
      const ratio = clamp((steps - from.at) / span, 0, 1);
      fillEnd = from.x + (to.x - from.x) * ratio;
    }
  }

  const visible = segments
    .map((segment, index) => ({ segment, index }))
    .filter(({ index }) => expandedView || index >= headIndex);

  return (
    <div
      ref={railRef}
      className={`pdm-rail${dragging ? ' is-drag' : ''}${className ? ` ${className}` : ''}`}
      style={width > 0 ? { ...style, '--pdm-u': `${width / SCENE_RATIO}px` } : style}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
    >
      <div className="pdm-rail__inner" style={{ transform: `translateX(${-off}px)` }}>
        <div
          className="pdm-rail__track"
          style={{ left: `${trackStart}px`, width: `${Math.max(0, trackEnd - trackStart)}px` }}
        >
          <div className="pdm-rail__fill" style={{ width: `${Math.max(0, fillEnd - trackStart)}px` }} />
        </div>

        <div
          className={`pdm-rail__skip${expandedView || lastIndex - headIndex <= 2 ? ' is-off' : ''}`}
          style={{
            left: `${width * PB + width * 0.07}px`,
            width: `${Math.max(0, width * (PC - PB) - width * 0.14)}px`,
          }}
        >
          <i />
          <i />
          <i />
        </div>

        {visible.map(({ segment, index }) => {
          const isLast = index === lastIndex;
          const showQuestion = isLast && segment.status !== 'DONE' && steps < segment.steps;
          const classNames = [
            'pdm-node',
            statusClass(segment.status),
            segment.needsAd ? 'is-ad' : '',
            isLast ? 'is-last' : '',
            isHidden(index) ? 'is-hid' : '',
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <button
              key={segment.segmentNo}
              type="button"
              className={classNames}
              style={{ left: `${xOf(index)}px` }}
              onClick={handleNodeClick(segment)}
              aria-label={`${segment.steps.toLocaleString('ko-KR')}걸음 ${segment.rewardLabel ?? ''}`}
            >
              <span className="pdm-node__dot">
                {showQuestion ? (
                  <span className="pdm-node__qm">?</span>
                ) : (
                  <img className="pdm-node__ico" src={iconOf(segment, isLast)} alt="" />
                )}
                {segment.needsAd && <i className="pdm-node__ad">AD</i>}
              </span>
              <b className="pdm-node__step">{segment.steps.toLocaleString('ko-KR')}걸음</b>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default PedometerRewardRail;
